import React, { useMemo, useState } from "react";
import { MAX_PAGE_SIZE } from "@/constants/pagination";
import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import { FiType, FiLayers } from "react-icons/fi";
import { useModules } from "@/api/features/modules";

export type UpsertModule = {
  name: string;
  parentId?: number | null;
};

type Props = {
  onSubmit: (payload: UpsertModule) => void | Promise<void>;
  defaultValues?: Partial<UpsertModule>;
  /** 🔹 Call this to close the modal and return to the page it was opened from */
  onCancel?: () => void;
};

export default function CreateModuleForm({
  onSubmit,
  defaultValues,
  onCancel,
}: Props) {
  const [form, setForm] = useState<UpsertModule>({
    name: defaultValues?.name ?? "",
    parentId:
      typeof defaultValues?.parentId === "number" ? defaultValues.parentId : null,
  });
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // fetch root candidates (safe: limit <= backend max)
  const { data, isLoading } = useModules({ page: 1, limit: MAX_PAGE_SIZE });

  // normalize + filter to roots
  const parentOptions = useMemo(() => {
    const items = Array.isArray(data?.items) ? (data!.items as any[]) : [];
    const normalized = items.map((m) => ({
      id: Number(m.id),
      name: String(m.name ?? ""),
      parentId:
        typeof m.parentId === "number" || m.parentId === null
          ? m.parentId
          : typeof m.parent_id === "number" || m.parent_id === null
          ? m.parent_id
          : null,
    }));
    const roots = normalized
      .filter((m) => m.parentId == null)
      .sort((a, b) => a.name.localeCompare(b.name));
    return roots;
  }, [data]);

  const change = <K extends keyof UpsertModule>(k: K, v: UpsertModule[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const name = form.name.trim();
    if (!name) return;

    const parentId =
      typeof form.parentId === "number" && !Number.isNaN(form.parentId)
        ? form.parentId
        : null;

    try {
      setPending(true);
      await onSubmit({ name, parentId });
    } catch (e: any) {
      setErr(e?.message ?? "Failed to create module.");
    } finally {
      setPending(false);
    }
  };

  const cancel = () => {
    if (typeof onCancel === "function") {
      onCancel();
    }
    // else no-op; parent should supply onCancel when used inside a modal
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field label="Module name" icon={<FiType />}>
        <Input
          value={form.name}
          onChange={(e) => change("name", e.target.value)}
          placeholder="e.g., Users & Access"
          required
        />
      </Field>

      <Field label="Parent module (optional)" icon={<FiLayers />}>
        <select
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50"
          value={typeof form.parentId === "number" ? String(form.parentId) : ""}
          onChange={(e) =>
            change("parentId", e.target.value ? Number(e.target.value) : null)
          }
          disabled={isLoading || pending}
          aria-label="Select parent module"
        >
          <option value="">— No parent (top-level) —</option>
          {isLoading ? (
            <option disabled>Loading…</option>
          ) : parentOptions.length > 0 ? (
            parentOptions.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))
          ) : (
            <option disabled>No eligible parents found</option>
          )}
        </select>
      </Field>

      {err && (
        <div className="sm:col-span-2 text-sm text-red-600">{err}</div>
      )}

      <div className="sm:col-span-2 mt-2 flex justify-end gap-2">
        <button
          type="button"
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          onClick={cancel}
          disabled={pending}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
          disabled={isLoading || pending}
        >
          {pending ? "Creating…" : "Create Module"}
        </button>
      </div>
    </form>
  );
}
